// Mail im Aktivitäts-Feed der Lead-Seite (Runde 6, Agent 6B): Mail-Eintrag mit Absender, Text, Anhängen, Verlauf und den Knöpfen
// Antworten (R), Allen antworten (A) und Weiterleiten (F); der Komposer öffnet direkt unter der Mail. Dazu die Entwürfe des
// Nutzers (auch geplante Mails) als Zeilen oben im Feed. Der Feed (`components/lead/Feed.tsx`) ruft nur `mailTitel`,
// `mailZusatz`, `MailInhalt`, `MailEntwuerfeImFeed` und `ohneDoppelteThreads` auf.
import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ApiFehler, type Aktivitaet, type Kontakt } from "../api";
import { mailEntwuerfeApi, type EntwurfStatus, type MailEntwurf, type MailVerlaufNachricht } from "../api/mailEntwuerfe";
import { Icon } from "../components/Icon";
import { useMeldung } from "../components/Meldungen";
import { htmlZuText } from "../werkzeuge/html";
import { zeitpunktLang } from "../werkzeuge/zeit";
import { ENTWUERFE_GEAENDERT, istOffen } from "./entwurfLager";
import { htmlAnzeige, textTeilenZitat } from "./htmlSicher";
import { groesseText } from "./logik";
import "./mailFeed.css";

const EmailKomposer = lazy(() => import("./EmailKomposer").then((m) => ({ default: m.EmailKomposer })));

type Art = "antwort" | "allen" | "weiterleiten";

const zeichen = (a: Aktivitaet, k: string): string | null => {
  const w = a[k];
  return typeof w === "string" && w ? w : null;
};

const istEingehend = (a: Aktivitaet): boolean => a.direction === "incoming" || a.direction === "inbound" || a.status === "inbox";

/** Schlüssel des Verlaufs: bei Mails aus dem CRM `thread_key`, bei Mails aus Close die ID des EmailThread. */
const verlaufSchluessel = (a: Aktivitaet): string | null => zeichen(a, "thread_key") ?? zeichen(a, "thread_id") ?? (a._type === "EmailThread" ? a.id : null);

// --- Feed-Bausteine ---------------------------------------------------------------------------------------------------------

export function mailTitel(a: Aktivitaet): ReactNode {
  const betreff = zeichen(a, "subject") ?? zeichen(a, "latest_normalized_subject") ?? "(ohne Betreff)";
  return (
    <>
      {istEingehend(a) ? "E-Mail erhalten" : "E-Mail gesendet"}: <span className="ls-eintrag__wer">{betreff}</span>
    </>
  );
}

export function mailZusatz(a: Aktivitaet): ReactNode {
  const anzahl = typeof a.n_emails === "number" ? a.n_emails : null;
  const offen = Array.isArray(a.opens) ? a.opens.length : 0;
  return (
    <>
      {anzahl && anzahl > 1 ? <span className="ls-chip">{anzahl} Mails</span> : null}
      {a.status && a.status !== "sent" && a.status !== "inbox" ? <span className="ls-chip">{a.status === "draft" ? "Entwurf" : String(a.status)}</span> : null}
      {offen > 0 ? (
        <span className="ls-chip ls-chip--ok" title={`Der Empfänger hat die Mail ${offen} Mal geöffnet`}>
          geöffnet{offen > 1 ? ` · ${offen}×` : ""}
        </span>
      ) : null}
    </>
  );
}

/** Close legt zu jeder protokollierten Mail einen EmailThread an. Wo die Mails des Verlaufs im Feed stehen, ist er überflüssig. */
export function ohneDoppelteThreads(liste: Aktivitaet[]): Aktivitaet[] {
  const threads = new Set<string>();
  for (const a of liste) {
    if (a._type === "Email") {
      const t = zeichen(a, "thread_id");
      if (t) threads.add(t);
    }
  }
  return threads.size ? liste.filter((a) => !(a._type === "EmailThread" && threads.has(a.id))) : liste;
}

// --- Einklappen --------------------------------------------------------------------------------------------------------------

function Einklappen({ children, zeilen = 6 }: { children: ReactNode; zeilen?: number }) {
  const [auf, setAuf] = useState(false);
  const [lang, setLang] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || auf) return;
    const pruefe = () => setLang(el.scrollHeight > el.clientHeight + 4);
    pruefe();
    const ro = new ResizeObserver(pruefe);
    ro.observe(el);
    return () => ro.disconnect();
  }, [auf, children]);
  return (
    <div className="ls-klapp">
      <div ref={ref} className={`ls-klapp__inhalt${auf ? "" : " ls-klapp__inhalt--zu"}${!auf && lang ? " ls-klapp__inhalt--lang" : ""}`} style={{ ["--ls-zeilen" as string]: zeilen }}>
        {children}
      </div>
      {lang ? (
        <button type="button" className="ls-klapp__knopf" aria-expanded={auf} onClick={() => setAuf((x) => !x)}>
          {auf ? "Weniger" : "Mehr anzeigen"}
        </button>
      ) : null}
    </div>
  );
}

// --- Text einer Mail ---------------------------------------------------------------------------------------------------------

/** Text mit Zitat: das Zitat (frühere Nachrichten) bleibt zugeklappt hinter "…". */
function MailText({ html, text, zitatHtml }: { html: string | null; text: string | null; zitatHtml?: string | null }) {
  const [zitatAuf, setZitatAuf] = useState(false);
  const geteilt = useMemo(() => {
    if (html) {
      const sauber = htmlAnzeige(html);
      const t = textTeilenZitat(sauber);
      return { haupt: t.haupt, zitat: t.zitat || (zitatHtml ? htmlAnzeige(zitatHtml) : "") };
    }
    return { haupt: "", zitat: zitatHtml ? htmlAnzeige(zitatHtml) : "" };
  }, [html, zitatHtml]);
  return (
    <>
      <Einklappen zeilen={6}>
        {geteilt.haupt.trim() ? <div className="mail-text" dangerouslySetInnerHTML={{ __html: geteilt.haupt }} /> : <div className="mehrzeilig">{text || ""}</div>}
      </Einklappen>
      {geteilt.zitat.trim() ? (
        <>
          <button type="button" className="mail-zitatknopf" aria-expanded={zitatAuf} title="Frühere Nachrichten ein- oder ausblenden" onClick={() => setZitatAuf((x) => !x)}>
            …
          </button>
          {zitatAuf ? <div className="mail-text mail-text--zitat" dangerouslySetInnerHTML={{ __html: geteilt.zitat }} /> : null}
        </>
      ) : null}
    </>
  );
}

interface AnhangZeile {
  name: string;
  groesse: number | null;
  url: string | null;
}

function anhaengeVon(a: Aktivitaet, leadId: string): AnhangZeile[] {
  const roh = Array.isArray(a.attachments) ? (a.attachments as Record<string, unknown>[]) : [];
  const ergebnis: AnhangZeile[] = [];
  roh.forEach((x, nr) => {
    if (!x || typeof x !== "object" || x.inline_only) return;
    const name = String(x.filename ?? x.dateiname ?? "Datei");
    const groesse = typeof x.size === "number" ? x.size : null;
    if (typeof x.datei_id === "string") ergebnis.push({ name, groesse, url: `/api/mail/dateien/${encodeURIComponent(x.datei_id)}` });
    else if (x.url) ergebnis.push({ name, groesse, url: `/api/leads/${encodeURIComponent(leadId)}/dateien/${encodeURIComponent(`${a.id}~${nr}`)}` });
    else ergebnis.push({ name, groesse, url: null });
  });
  return ergebnis;
}

function Anhaenge({ liste }: { liste: AnhangZeile[] }) {
  if (liste.length === 0) return null;
  return (
    <div className="mail-anhaenge">
      {liste.map((d, i) =>
        d.url ? (
          <a key={`${d.name}-${i}`} className="chip mail-anhang" href={d.url} target="_blank" rel="noreferrer" title={`${d.name} herunterladen`}>
            <Icon name="kopieren" groesse={11} /> {d.name}
            {d.groesse ? <span className="km-leise"> {groesseText(d.groesse)}</span> : null}
          </a>
        ) : (
          <span key={`${d.name}-${i}`} className="chip mail-anhang mail-anhang--nur" title="Diese Datei liegt nur im Mailprogramm des Absenders">
            {d.name}
          </span>
        ),
      )}
    </div>
  );
}

// --- Verlauf -----------------------------------------------------------------------------------------------------------------

function VerlaufListe({ schluessel, leadId, aktuell }: { schluessel: string; leadId: string; aktuell: string }) {
  const [daten, setDaten] = useState<MailVerlaufNachricht[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [auf, setAuf] = useState<Record<string, boolean>>({});
  useEffect(() => {
    let weg = false;
    mailEntwuerfeApi
      .verlauf(schluessel, leadId)
      .then((r) => !weg && setDaten(r.nachrichten))
      .catch((e) => !weg && setFehler(e instanceof Error ? e.message : "Verlauf nicht geladen."));
    return () => {
      weg = true;
    };
  }, [schluessel, leadId]);
  if (fehler) return <div className="km-fehlertext">{fehler}</div>;
  if (!daten) return <div className="km-leise">Verlauf wird geladen …</div>;
  return (
    <ol className="mail-verlauf" aria-label="Alle Mails dieses Verlaufs">
      {daten.map((n) => {
        const offen = auf[n.activity_id] ?? n.activity_id === aktuell;
        const wer = n.von.name ? `${n.von.name} <${n.von.adresse}>` : n.von.adresse;
        return (
          <li key={n.activity_id} className={`mail-verlauf__eintrag${n.activity_id === aktuell ? " mail-verlauf__eintrag--jetzt" : ""}`}>
            <button type="button" className="mail-verlauf__kopf" aria-expanded={offen} onClick={() => setAuf((x) => ({ ...x, [n.activity_id]: !offen }))}>
              <Icon name={n.richtung === "eingehend" ? "eingehend" : "ausgehend"} groesse={11} />
              <span className="mail-verlauf__wer">{wer}</span>
              <span className="km-leise">{n.datum ? zeitpunktLang(n.datum) : ""}</span>
              {n.dateien.length > 0 ? <span className="km-leise">· {n.dateien.length} Anhang{n.dateien.length > 1 ? "e" : ""}</span> : null}
            </button>
            {offen ? (
              <div className="mail-verlauf__koerper">
                <MailText html={n.html} text={n.text} />
                <Anhaenge liste={n.dateien.filter((d) => !d.inline_cid).map((d) => ({ name: d.dateiname, groesse: d.groesse, url: d.url }))} />
              </div>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

// --- Inhalt eines Mail-Eintrags --------------------------------------------------------------------------------------------------

interface InhaltProps {
  a: Aktivitaet;
  leadId: string;
  kontakte: Kontakt[];
  /** die geladenen Einträge des Feeds (für die Zahl der Mails im Verlauf) */
  eintraege: Aktivitaet[];
  neu: () => void;
}

export function MailInhalt({ a, leadId, kontakte, eintraege, neu }: InhaltProps) {
  const [art, setArt] = useState<Art | null>(null);
  const [verlaufAuf, setVerlaufAuf] = useState(false);
  const [detail, setDetail] = useState(false);
  const rahmen = useRef<HTMLDivElement>(null);

  const von = zeichen(a, "sender");
  const an = Array.isArray(a.to) ? (a.to as string[]) : [];
  const cc = Array.isArray(a.cc) ? (a.cc as string[]) : [];
  const bcc = Array.isArray(a.bcc) ? (a.bcc as string[]) : [];
  const html = zeichen(a, "body_html");
  const text = zeichen(a, "body_text") ?? zeichen(a, "body_preview") ?? (html ? htmlZuText(html) : null);
  const schluessel = verlaufSchluessel(a);
  const geschwister = schluessel ? eintraege.filter((x) => x._type === "Email" && verlaufSchluessel(x) === schluessel).length : 0;
  const mailsImVerlauf = Math.max(geschwister, typeof a.n_emails === "number" ? a.n_emails : 0);
  const anhaenge = anhaengeVon(a, leadId);
  const entwurfInClose = a.status === "draft";

  const oeffnen = useCallback((w: Art) => setArt((alt) => (alt === w ? null : w)), []);

  const taste = (e: React.KeyboardEvent) => {
    const ziel = e.target as HTMLElement;
    if (e.metaKey || e.ctrlKey || e.altKey || ziel.closest("input, textarea, select, [contenteditable='true']")) return;
    if (art) return;
    const k = e.key.toLowerCase();
    if (k === "r") oeffnen("antwort");
    else if (k === "a") oeffnen("allen");
    else if (k === "f") oeffnen("weiterleiten");
    else return;
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div ref={rahmen} className="mail-eintrag" tabIndex={0} onKeyDown={taste} aria-label="E-Mail. Mit R antworten, mit A allen antworten, mit F weiterleiten.">
      {von || an.length ? (
        <div className="ls-eintrag__meta mail-meta">
          {von ? `Von ${von}` : ""}
          {von && an.length ? " · " : ""}
          {an.length ? `An ${an.join(", ")}` : ""}
          {cc.length || bcc.length ? (
            <button type="button" className="km-link mail-meta__mehr" aria-expanded={detail} onClick={() => setDetail((x) => !x)}>
              {detail ? " weniger" : " mehr"}
            </button>
          ) : null}
          {detail ? (
            <div className="mail-meta__detail">
              {cc.length ? <div>Cc {cc.join(", ")}</div> : null}
              {bcc.length ? <div>Bcc {bcc.join(", ")}</div> : null}
            </div>
          ) : null}
        </div>
      ) : null}
      {html || text ? <MailText html={html} text={text} zitatHtml={zeichen(a, "body_html_quoted")} /> : null}
      <Anhaenge liste={anhaenge} />
      <div className="mail-aktionen">
        {!entwurfInClose ? (
          <>
            <button type="button" className={`knopf knopf--klein knopf--leise${art === "antwort" ? " mail-aktion--an" : ""}`} aria-pressed={art === "antwort"} title="Antworten (R)" onClick={() => oeffnen("antwort")}>
              <Icon name="rueckgaengig" groesse={12} /> Antworten
            </button>
            <button type="button" className={`knopf knopf--klein knopf--leise${art === "allen" ? " mail-aktion--an" : ""}`} aria-pressed={art === "allen"} title="Allen antworten (A)" onClick={() => oeffnen("allen")}>
              Allen antworten
            </button>
            <button type="button" className={`knopf knopf--klein knopf--leise${art === "weiterleiten" ? " mail-aktion--an" : ""}`} aria-pressed={art === "weiterleiten"} title="Weiterleiten (F)" onClick={() => oeffnen("weiterleiten")}>
              <Icon name="pfeil-rechts" groesse={12} /> Weiterleiten
            </button>
          </>
        ) : null}
        {schluessel && mailsImVerlauf > 1 ? (
          <button type="button" className="km-link mail-aktionen__verlauf" aria-expanded={verlaufAuf} onClick={() => setVerlaufAuf((x) => !x)}>
            {verlaufAuf ? "Verlauf ausblenden" : `Verlauf anzeigen (${mailsImVerlauf} Mails)`}
          </button>
        ) : null}
      </div>
      {verlaufAuf && schluessel ? <VerlaufListe schluessel={schluessel} leadId={leadId} aktuell={a.id} /> : null}
      {art ? (
        <Suspense fallback={<div className="km-leise">Wird geladen …</div>}>
          <EmailKomposer
            key={art}
            leadId={leadId}
            kontakte={kontakte}
            antwort={{ art, activityId: a.id }}
            imFeed
            onClose={() => {
              setArt(null);
              rahmen.current?.focus();
            }}
            onGesendet={neu}
          />
        </Suspense>
      ) : null}
    </div>
  );
}

// --- Entwürfe im Feed ------------------------------------------------------------------------------------------------------------

const SICHTBAR: EntwurfStatus[] = ["entwurf", "geplant", "fehler", "sendet"];
const ART_TEXT: Record<string, string> = { antwort: "Antwort", allen: "Antwort an alle", weiterleiten: "Weiterleitung" };

function vorschauText(e: MailEntwurf): string {
  const roh = (e.body_html ?? "").replace(/<div class="crm-zitat">[\s\S]*$/i, "");
  return htmlZuText(roh).replace(/\s+/g, " ").trim().slice(0, 160);
}

export function MailEntwuerfeImFeed({ leadId, kontakte, onGesendet }: { leadId: string; kontakte: Kontakt[]; onGesendet: () => void }) {
  const meldung = useMeldung();
  const [liste, setListe] = useState<MailEntwurf[]>([]);
  const [bearbeiten, setBearbeiten] = useState<string | null>(null);
  const [arbeit, setArbeit] = useState<string | null>(null);
  const [takt, setTakt] = useState(0);

  const laden = useCallback(async () => {
    try {
      const r = await mailEntwuerfeApi.liste({ lead_id: leadId, status: SICHTBAR, limit: 50 });
      setListe(r.data);
    } catch {
      /* der Feed funktioniert auch ohne Entwürfe */
    }
  }, [leadId]);

  useEffect(() => {
    setListe([]);
    void laden();
  }, [laden]);

  useEffect(() => {
    const neuLaden = () => {
      setTakt((t) => t + 1);
      void laden();
    };
    window.addEventListener(ENTWUERFE_GEAENDERT, neuLaden);
    window.addEventListener("focus", neuLaden);
    return () => {
      window.removeEventListener(ENTWUERFE_GEAENDERT, neuLaden);
      window.removeEventListener("focus", neuLaden);
    };
  }, [laden]);

  const sichtbar = liste.filter((e) => e.id === bearbeiten || !istOffen(e.id));
  void takt;
  if (sichtbar.length === 0) return null;

  const verwerfen = async (e: MailEntwurf) => {
    setArbeit(e.id);
    try {
      await mailEntwuerfeApi.loeschen(e.id);
      setListe((l) => l.filter((x) => x.id !== e.id));
      meldung.ok("Entwurf verworfen.");
    } catch (err) {
      meldung.fehler(err, "Entwurf nicht gelöscht");
    } finally {
      setArbeit(null);
    }
  };

  const planAufheben = async (e: MailEntwurf) => {
    setArbeit(e.id);
    try {
      const r = await mailEntwuerfeApi.planAufheben(e.id);
      setListe((l) => l.map((x) => (x.id === e.id ? r.entwurf : x)));
      meldung.ok("Planung aufgehoben. Die Mail bleibt als Entwurf.");
    } catch (err) {
      meldung.fehler(err, "Planung nicht aufgehoben");
    } finally {
      setArbeit(null);
    }
  };

  const jetztSenden = async (e: MailEntwurf) => {
    setArbeit(e.id);
    try {
      const r = await mailEntwuerfeApi.senden(e.id);
      meldung.ok(`E-Mail gesendet an ${(r.email?.an ?? []).join(", ")}.`);
      setListe((l) => l.filter((x) => x.id !== e.id));
      onGesendet();
    } catch (err) {
      if (err instanceof ApiFehler && err.code === "bestaetigung_noetig") {
        setBearbeiten(e.id); // die Hinweise zeigt der Komposer
      } else {
        meldung.fehler(err, "Nicht gesendet");
      }
      void laden();
    } finally {
      setArbeit(null);
    }
  };

  return (
    <>
      {sichtbar.map((e) => {
        if (e.id === bearbeiten) {
          return (
            <li key={e.id} className="ls-eintrag ls-eintrag--email ls-eintrag--bearbeiten mail-entwurf">
              <span className="ls-symbol ls-symbol--email" aria-hidden="true">
                <Icon name="mail" groesse={12} />
              </span>
              <div className="ls-eintrag__inhalt">
                <Suspense fallback={<div className="km-leise">Wird geladen …</div>}>
                  <EmailKomposer
                    leadId={leadId}
                    kontakte={kontakte}
                    entwurfId={e.id}
                    imFeed
                    onClose={() => {
                      setBearbeiten(null);
                      void laden();
                    }}
                    onGesendet={onGesendet}
                  />
                </Suspense>
              </div>
            </li>
          );
        }
        const geplant = e.status === "geplant";
        const ueberfaellig = geplant && e.geplant_at && new Date(e.geplant_at).getTime() < Date.now();
        const an = e.an.map((a) => a.name || a.adresse).join(", ");
        return (
          <li key={e.id} className={`ls-eintrag ls-eintrag--email ls-eintrag--entwurf mail-entwurf${arbeit === e.id ? " mail-entwurf--arbeit" : ""}`} data-entwurf={e.id}>
            <span className="ls-symbol ls-symbol--email" aria-hidden="true">
              <Icon name={geplant ? "uhr" : "stift"} groesse={12} />
            </span>
            <div className="ls-eintrag__inhalt">
              <div className="ls-eintrag__kopf">
                <span className="ls-eintrag__titel">
                  {geplant ? "E-Mail geplant" : e.status === "sendet" ? "E-Mail wird gesendet" : "E-Mail-Entwurf"}: <span className="ls-eintrag__wer">{e.betreff || "(ohne Betreff)"}</span>
                </span>
                <span className="ls-eintrag__zusatz">
                  {ART_TEXT[e.art] ? <span className="ls-chip">{ART_TEXT[e.art]}</span> : null}
                  {geplant && e.geplant_at ? (
                    <span className={`ls-chip${ueberfaellig ? " ls-chip--entwurf" : ""}`} title={ueberfaellig ? "Der Zeitpunkt ist vorbei. Das automatische Senden ist nicht freigegeben oder die Mail hängt: bitte von Hand senden." : undefined}>
                      {ueberfaellig ? "überfällig · " : "für "}
                      {zeitpunktLang(e.geplant_at)}
                    </span>
                  ) : null}
                  {e.status === "fehler" ? <span className="ls-chip ls-chip--entwurf">nicht gesendet</span> : null}
                </span>
                <span className="ls-eintrag__rechts">
                  <time className="ls-eintrag__zeit" title={e.date_updated ? zeitpunktLang(e.date_updated) : undefined}>
                    {e.date_updated ? zeitpunktLang(e.date_updated) : ""}
                  </time>
                </span>
              </div>
              <div className="ls-eintrag__koerper">
                {an ? <div className="ls-eintrag__meta">An {an}</div> : <div className="ls-eintrag__meta">Noch kein Empfänger</div>}
                {e.fehler ? <div className="km-fehlertext">{e.fehler}</div> : null}
                {vorschauText(e) ? <div className="mail-entwurf__vorschau">{vorschauText(e)}</div> : null}
                {e.dateien.filter((d) => !d.inline_cid).length > 0 ? <Anhaenge liste={e.dateien.filter((d) => !d.inline_cid).map((d) => ({ name: d.dateiname, groesse: d.groesse, url: d.url }))} /> : null}
                {e.status !== "sendet" ? (
                  <div className="mail-aktionen">
                    <button type="button" className="knopf knopf--klein" disabled={arbeit === e.id} onClick={() => setBearbeiten(e.id)}>
                      {geplant ? "Ansehen und ändern" : "Weiterschreiben"}
                    </button>
                    {geplant || e.status === "fehler" ? (
                      <button type="button" className="knopf knopf--klein knopf--leise" disabled={arbeit === e.id} onClick={() => void planAufheben(e)}>
                        Planung aufheben
                      </button>
                    ) : null}
                    {geplant || e.status === "fehler" ? (
                      <button type="button" className="knopf knopf--klein knopf--leise" disabled={arbeit === e.id} onClick={() => void jetztSenden(e)}>
                        Jetzt senden
                      </button>
                    ) : null}
                    <button type="button" className="knopf knopf--klein knopf--leise" disabled={arbeit === e.id} onClick={() => void verwerfen(e)}>
                      Verwerfen
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </li>
        );
      })}
    </>
  );
}
